import {
  DEFAULT_TRANSITION_DURATION,
  EVENT_KZ_OVERLAY_CLOSED,
  EVENT_KZ_OVERLAY_OPENED,
  KzOverlayEventDetail,
} from "kz-ui-sdk";

type OverlayOpenedListener = (detail: KzOverlayEventDetail) => void;

class OverlayPresenceStore {
  private static instance: OverlayPresenceStore | null = null;

  private openIds = new Set<string>();
  private pendingRemoval = new Map<string, ReturnType<typeof setTimeout>>();
  private listeners = new Set<() => void>();
  private openedListeners = new Set<OverlayOpenedListener>();
  private snapshot: ReadonlySet<string> = new Set();

  private constructor() {
    if (typeof window === "undefined") return;
    window.addEventListener(EVENT_KZ_OVERLAY_OPENED, this.handleOpen);
    window.addEventListener(EVENT_KZ_OVERLAY_CLOSED, this.handleClose);
  }

  static getInstance(): OverlayPresenceStore {
    if (!OverlayPresenceStore.instance) {
      OverlayPresenceStore.instance = new OverlayPresenceStore();
    }
    return OverlayPresenceStore.instance;
  }

  private handleOpen = (event: Event) => {
    const detail = (event as CustomEvent<KzOverlayEventDetail>).detail;
    const overlayId = detail?.overlayId;
    if (!overlayId) return;

    const pending = this.pendingRemoval.get(overlayId);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.pendingRemoval.delete(overlayId);
      return;
    }

    if (this.openIds.has(overlayId)) return;
    this.openIds.add(overlayId);
    this.refreshSnapshot();
    this.notify();
    this.openedListeners.forEach((listener) => listener(detail));
  };

  private handleClose = (event: Event) => {
    const overlayId = (event as CustomEvent<KzOverlayEventDetail>).detail?.overlayId;
    if (!overlayId || !this.openIds.has(overlayId)) return;

    if (this.pendingRemoval.has(overlayId)) return;

    const handle = setTimeout(() => {
      this.openIds.delete(overlayId);
      this.pendingRemoval.delete(overlayId);
      this.refreshSnapshot();
      this.notify();
    }, DEFAULT_TRANSITION_DURATION);
    this.pendingRemoval.set(overlayId, handle);
  };

  private refreshSnapshot = () => {
    this.snapshot = new Set(this.openIds);
  };

  private notify = () => {
    this.listeners.forEach((listener) => listener());
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Fires once per genuine open. SDK same-tick re-dispatches and reopens within
  // the transition window are absorbed here, same as the snapshot.
  onOpened = (listener: OverlayOpenedListener): (() => void) => {
    this.openedListeners.add(listener);
    return () => {
      this.openedListeners.delete(listener);
    };
  };

  getSnapshot = (): ReadonlySet<string> => this.snapshot;
}

OverlayPresenceStore.getInstance();

export default OverlayPresenceStore;
